import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/api";
import { RatePackage } from "@/types";

const inputCls = "w-full border border-stone-300 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none";
const btnCls = "border border-stone-300 px-2 py-1 text-xs font-semibold hover:bg-stone-50";
const EMPTY = { name: "", price_label: "", kind: "individual", inclusionsText: "", published: true };

export default function AdminPackages() {
  const [packages, setPackages] = useState<RatePackage[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/packages").then((r) => setPackages(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const startEdit = (p: RatePackage) => {
    setForm({ name: p.name, price_label: p.price_label, kind: p.kind, inclusionsText: p.inclusions.join("\n"), published: p.published });
    setEditing(p.id);
  };

  const save = async () => {
    if (!form.name.trim() || !form.price_label.trim()) { toast.error("Name and price are required"); return; }
    const body = {
      name: form.name, price_label: form.price_label, kind: form.kind,
      inclusions: form.inclusionsText.split("\n").map((s) => s.trim()).filter(Boolean),
      published: form.published,
    };
    try {
      if (editing === "new") await api.post("/admin/packages", body);
      else await api.put(`/admin/packages/${editing}`, body);
      toast.success("Package saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (p: RatePackage) => {
    if (!window.confirm(`Delete package "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/packages/${p.id}`);
      toast.success("Package deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const togglePublished = async (p: RatePackage) => {
    try {
      await api.put(`/admin/packages/${p.id}`, { name: p.name, price_label: p.price_label, kind: p.kind, inclusions: p.inclusions, published: !p.published });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-packages">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold text-stone-900">Packages ({packages.length})</h2>
        <button data-testid="admin-add-package" onClick={() => { setForm(EMPTY); setEditing("new"); }} className="flex items-center gap-1 bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-stone-700">
          <Plus size={13} /> Add package
        </button>
      </div>

      {editing && (
        <div data-testid="package-form" className="mb-5 border border-stone-300 bg-stone-50 p-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Name</label>
              <input data-testid="package-form-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Price label</label>
              <input data-testid="package-form-price" className={inputCls} value={form.price_label} onChange={(e) => setForm({ ...form, price_label: e.target.value })} placeholder="$1,500/mo" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Kind</label>
              <select data-testid="package-form-kind" className={inputCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                <option value="subscription">subscription</option>
                <option value="individual">individual</option>
                <option value="custom">custom</option>
              </select>
            </div>
          </div>
          <label className="mb-1 mt-3 block text-xs font-semibold text-stone-600">Inclusions (one per line)</label>
          <textarea data-testid="package-form-inclusions" rows={5} className={inputCls} value={form.inclusionsText} onChange={(e) => setForm({ ...form, inclusionsText: e.target.value })} />
          <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-stone-700">
            <input data-testid="package-form-published" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            Published (visible on site)
          </label>
          <div className="mt-4 flex gap-2">
            <button data-testid="package-form-save" onClick={save} className="bg-stone-900 px-4 py-2 text-xs font-semibold text-white hover:bg-stone-700">Save</button>
            <button data-testid="package-form-cancel" onClick={() => setEditing(null)} className={btnCls}>Cancel</button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {packages.map((p) => (
          <div key={p.id} data-testid={`package-row-${p.id}`} className="flex flex-wrap items-center gap-2 border border-stone-200 px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-stone-900">{p.name} — {p.price_label}</p>
              <p className="truncate text-xs text-stone-500">{p.kind} · {p.inclusions.length} inclusions</p>
            </div>
            <button data-testid={`package-published-${p.id}`} onClick={() => togglePublished(p)} className={`border px-2 py-1 text-[10px] font-bold ${p.published ? "border-green-400 bg-green-100 text-green-800" : "border-stone-300 text-stone-400"}`}>
              {p.published ? "LIVE" : "HIDDEN"}
            </button>
            <button data-testid={`package-edit-${p.id}`} onClick={() => startEdit(p)} className={btnCls}><Pencil size={13} /></button>
            <button data-testid={`package-delete-${p.id}`} onClick={() => del(p)} className="border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
